import React, { useMemo, useState } from "react";
import OrbitalLoader from "../../components/common/OrbitalLoader";
import SampleVideoRow from "../../components/patterns/SampleVideoRow";

const SORT_OPTIONS = [
  { value: "views", label: "Views" },
  { value: "creator_views_outlier", label: "Views vs creator" },
  { value: "likes", label: "Likes" },
  { value: "creator_likes_outlier", label: "Likes vs creator" },
  { value: "comments", label: "Comments" },
  { value: "creator_comments_outlier", label: "Comments vs creator" },
  { value: "engagement", label: "Engagement" },
  { value: "creator_engagement_outlier", label: "Engagement vs creator" },
];

const getSortValue = (row, key) => {
  if (key === "views") return row?.video?.count_views ?? -Infinity;
  if (key === "likes") return row?.video?.count_likes ?? -Infinity;
  if (key === "comments") return row?.video?.count_comments ?? -Infinity;
  if (key === "engagement") {
    const e = row?.video?.engagement;
    return e != null && !Number.isNaN(Number(e)) ? Number(e) : -Infinity;
  }
  const v = Number(row?.[key]);
  return Number.isNaN(v) ? -Infinity : v;
};

const PatternExamples = ({ patternVideoRows, loadingVideos, formatNumber, baseUrl }) => {
  const [sortBy, setSortBy] = useState("views");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredAndSortedRows = useMemo(() => {
    const query = (searchQuery || "").trim().toLowerCase();
    let rows = [...patternVideoRows];
    if (query) {
      rows = rows.filter((row) =>
        (row?.video?.title || "").toLowerCase().includes(query)
      );
    }
    rows.sort((a, b) => {
      const va = getSortValue(a, sortBy);
      const vb = getSortValue(b, sortBy);
      return vb - va;
    });
    return rows;
  }, [patternVideoRows, searchQuery, sortBy]);

  if (loadingVideos) {
    return (
      <div className="flex justify-center py-10 text-theme-text">
        <OrbitalLoader size={62} text="Loading videos..." />
      </div>
    );
  }

  if (patternVideoRows.length === 0) {
    return (
      <div className="py-8 text-center text-theme-text opacity-70 rounded-lg border border-theme-secondary bg-theme-main">
        No videos for this pattern.
      </div>
    );
  }

  return (
    <div className="space-y-4 overflow-visible">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 overflow-visible">
        <h4 className="text-sm font-semibold text-theme-text">
          {filteredAndSortedRows.length} video{filteredAndSortedRows.length !== 1 ? "s" : ""}
        </h4>
        <div className="flex flex-wrap items-center gap-2 overflow-visible p-0.5">
          <input
            type="search"
            placeholder="Search by title..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="px-3 py-1.5 text-sm rounded-md border border-theme-secondary bg-theme-main text-theme-text placeholder-theme-text placeholder-opacity-50 focus:outline-none focus:ring-2 focus:ring-theme-primary min-w-[180px]"
            aria-label="Search by title"
          />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-1.5 text-sm rounded-md border border-theme-secondary bg-theme-main text-theme-text focus:outline-none focus:ring-2 focus:ring-theme-primary"
            aria-label="Sort by"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-4">
        {filteredAndSortedRows.map((row) => (
          <SampleVideoRow
            key={row.video?.id ?? row.hash}
            video={row.video}
            creator={row.creator}
            creatorViewsOutlier={row.creator_views_outlier}
            creatorLikesOutlier={row.creator_likes_outlier}
            creatorCommentsOutlier={row.creator_comments_outlier}
            creatorViewsAll={row.creator_views_all}
            creatorLikesAll={row.creator_likes_all}
            creatorCommentsAll={row.creator_comments_all}
            content={row.content ?? {}}
            formatNumber={formatNumber}
            videoDetailUrl={
              row.hash && baseUrl
                ? `/audience/${baseUrl.split("/")[2]}/videos/${row.hash}/`
                : null
            }
          />
        ))}
      </div>

      {filteredAndSortedRows.length === 0 && searchQuery.trim() && (
        <div className="py-6 text-center text-theme-text opacity-60 text-sm">
          No videos match &quot;{searchQuery.trim()}&quot;.
        </div>
      )}
    </div>
  );
};

export default PatternExamples;
